import { Tag } from 'lucide-react'
import { useState } from 'react'

import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import { Textarea } from '@/components/ui/textarea'
import { SelectFilter } from '@/containers/billing/register/billingFilters/filter/SelectFilter'
import { NewClientData } from '@/containers/billing/register/billingPortals/newRegisterWizard/NewRegisterWizard'
import {
  CountryPhoneSelector,
  CountrySelector,
} from '@/containers/billing/register/billingPortals/newRegisterWizard/steps/components'
import { Country } from '@/interfaces/billing'
import { cn } from '@/lib/utils'

import { useCustomerCategories } from '../hooks/useCustomerCategories'
import { useMunicipalitiesList } from '../hooks/useMunicipalitiesList'
import { useProvincesList } from '../hooks/useProvincesList'
import { ClientValidationErrors } from '../types'

interface NewClientFormProps {
  clientData: NewClientData
  onClientDataChange: (updates: Partial<NewClientData>) => void
  onSwitchToSearch: () => void
  validationErrors: ClientValidationErrors
  onFieldChange: (field: keyof ClientValidationErrors) => void
  onFieldFocus?: (field: string) => void
  disabled?: boolean
  className?: string
  clientContainer?: boolean
}

// Error display component
const ErrorText = ({
  error,
  className,
}: {
  error?: string
  className?: string
}) => {
  if (!error) return null
  return <p className={cn('text-sm text-red-500', className)}>{error}</p>
}

export function NewClientForm({
  clientData,
  onClientDataChange,
  onSwitchToSearch,
  validationErrors,
  onFieldChange,
  onFieldFocus,
  disabled = false,
  className,
  clientContainer = false,
}: NewClientFormProps) {
  const {
    categoryOptions,
    categoriesToStringArray,
    stringArrayToCategories,
    isLoading: isLoadingCategories,
    hasError: hasCategoriesError,
    error: categoriesError,
  } = useCustomerCategories()

  const {
    provinceOptions,
    isLoadingProvinces,
    isErrorProvinces,
    errorProvinces,
  } = useProvincesList({
    countryId: clientData.country?.id ? parseInt(clientData.country.id) : 54,
  })
  const {
    municipalityOptions,
    isLoadingMunicipalities,
    isErrorMunicipalities,
    errorMunicipalities,
  } = useMunicipalitiesList({
    provinceId: clientData.province?.id ? parseInt(clientData.province.id) : 0,
  })

  const handleInputChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
  ) => {
    const { name, value } = e.target
    onFieldChange(name as keyof ClientValidationErrors)
    onClientDataChange({ [name]: value })
  }

  const handleInputFocus = (field: string) => {
    onFieldFocus?.(field)
  }

  const handleSelectChange = (name: keyof NewClientData, value: string) => {
    onFieldChange(name as keyof ClientValidationErrors)

    if (name === 'province') {
      onClientDataChange({
        province: {
          id: value,
          name: provinceOptions.find((p) => p.value === value)?.label || '',
        },
        municipality: {
          id: '',
          name: '',
        }, // Reset municipality
      })
    } else if (name === 'municipality') {
      onClientDataChange({
        municipality: {
          id: value,
          name: municipalityOptions.find((m) => m.value === value)?.label || '',
        },
      })
    }
  }

  const handlePhoneChange = (phoneData: {
    number: string
    codeCountry: string
  }) => {
    onFieldChange('phone')
    onClientDataChange({ phone: phoneData })
  }

  const handleCountryChange = (country: Country) => {
    onFieldChange('country')
    onClientDataChange({
      country: {
        id: country.id.toString(),
        name: country.name,
      },
      province: {
        id: '',
        name: '',
      },
      municipality: {
        id: '',
        name: '',
      },
    })
  }

  const handleCategoryChange = (selectedCategories: string[]) => {
    onFieldChange('categories')
    const categoryIds = stringArrayToCategories(selectedCategories)
    onClientDataChange({ categories: categoryIds })
  }

  return (
    <div className={className}>
      <div className="mb-4 flex items-center justify-between">
        <h3 className="text-lg font-medium">
          {clientContainer ? 'Generales' : 'Datos del Nuevo Cliente'}
        </h3>
        {!clientContainer && (
          <Button
            variant="outline"
            size="sm"
            disabled={disabled}
            onClick={onSwitchToSearch}
          >
            Buscar Existente
          </Button>
        )}
      </div>

      {/* Show loading/error states for dependencies */}
      {isLoadingProvinces ||
        (isLoadingMunicipalities && (
          <div className="mb-4 text-sm text-muted-foreground">
            Cargando datos de ubicación...
          </div>
        ))}

      <div className="grid grid-cols-1 gap-x-4 gap-y-3 md:grid-cols-2">
        {/* Basic Info */}
        <div className="space-y-1">
          <Label htmlFor="name">Nombre (*)</Label>
          <Input
            id="name"
            name="name"
            value={clientData.name}
            onChange={handleInputChange}
            onFocus={() => handleInputFocus('name')}
            className={validationErrors.name ? 'border-red-500' : ''}
            disabled={disabled}
            required
          />
          <ErrorText error={validationErrors.name} />
        </div>

        <div className="space-y-1">
          <Label htmlFor="lastName">Apellidos (*)</Label>
          <Input
            id="lastName"
            name="lastName"
            value={clientData.lastName || ''}
            onChange={handleInputChange}
            onFocus={() => handleInputFocus('lastName')}
            className={validationErrors.lastName ? 'border-red-500' : ''}
            disabled={disabled}
            required
          />
          <ErrorText error={validationErrors.lastName} />
        </div>

        <div className="space-y-1">
          <Label htmlFor="idNumber">No. de Identificación</Label>
          <Input
            id="idNumber"
            name="idNumber"
            value={clientData.idNumber || ''}
            onChange={handleInputChange}
            disabled={disabled}
          />
        </div>

        <div className="space-y-1">
          <Label htmlFor="phone">Teléfono (*)</Label>
          <CountryPhoneSelector
            value={clientData.phone}
            onChange={handlePhoneChange}
            className={
              validationErrors.phone ? 'rounded-md border-red-500' : ''
            }
            disabled={disabled}
          />
          <ErrorText error={validationErrors.phone} />
        </div>

        <div className="space-y-1">
          <Label htmlFor="email">Correo electrónico</Label>
          <Input
            id="email"
            name="email"
            type="email"
            value={clientData.email || ''}
            className={validationErrors.email ? 'border-red-500' : ''}
            onChange={handleInputChange}
            onFocus={() => handleInputFocus('email')}
            disabled={disabled}
          />
          <ErrorText error={validationErrors.email} />
        </div>

        <div className="space-y-1">
          <Label htmlFor="contractNumber">No. de Contrato</Label>
          <Input
            id="contractNumber"
            name="contractNumber"
            value={clientData.contractNumber || ''}
            onChange={handleInputChange}
            disabled={disabled}
          />
        </div>

        <div className="space-y-1">
          <Label htmlFor="organization">Organización</Label>
          <Input
            id="organization"
            name="organization"
            value={clientData.organization || ''}
            onChange={handleInputChange}
            disabled={disabled}
          />
        </div>

        <div className="mt-1 flex flex-col gap-3">
          <Label htmlFor="categories">Categorías del Cliente</Label>
          <div onFocus={() => handleInputFocus('categories')}>
            <SelectFilter
              title="Categorías"
              placeholder="Buscar categorías..."
              selectionOptions={categoryOptions}
              activeSelections={categoriesToStringArray(clientData.categories)}
              onSelectionsChange={handleCategoryChange}
              searchActive={false}
              icon={<Tag className="h-4 w-4" />}
              className="w-fit rounded-md border"
            />
          </div>
          {isLoadingCategories && (
            <p className="text-sm text-muted-foreground">
              Cargando categorías...
            </p>
          )}
          {hasCategoriesError && (
            <p className="text-sm text-red-500">
              Error cargando categorías: {categoriesError?.message}
            </p>
          )}
        </div>

        <div className="space-y-1 md:col-span-2">
          <Label htmlFor="legalNotes">Notas Legales</Label>
          <Textarea
            id="legalNotes"
            name="legalNotes"
            value={clientData.legalNotes || ''}
            onChange={handleInputChange}
            rows={2}
            disabled={disabled}
          />
        </div>

        {/* Address Info */}
        <h4 className="text-md mt-3 border-t pt-4 font-medium md:col-span-2">
          Dirección
        </h4>

        <div className="space-y-1">
          <Label htmlFor="mainAddress">Calle Principal (*)</Label>
          <Input
            id="mainAddress"
            name="mainAddress"
            value={clientData.mainAddress || ''}
            onChange={handleInputChange}
            onFocus={() => handleInputFocus('mainAddress')}
            className={validationErrors.mainAddress ? 'border-red-500' : ''}
            disabled={disabled}
          />
          <ErrorText error={validationErrors.mainAddress} />
        </div>

        <div className="space-y-1">
          <Label htmlFor="secondaryAddress">Calle Secundaria</Label>
          <Input
            id="secondaryAddress"
            name="secondaryAddress"
            value={clientData.secondaryAddress || ''}
            onChange={handleInputChange}
            disabled={disabled}
          />
        </div>

        <div className="space-y-1">
          <Label htmlFor="locality">Localidad</Label>
          <Input
            id="locality"
            name="locality"
            value={clientData.locality || ''}
            onChange={handleInputChange}
            disabled={disabled}
          />
        </div>

        <div className="mt-[0.35rem] flex flex-col gap-2">
          <Label htmlFor="country">País (*)</Label>
          <CountrySelector
            value={clientData.country?.name || 'Cuba'}
            onChange={handleCountryChange}
            valueType="name"
            showFlag={true}
            showPrefix={false}
            placeholder="Seleccionar país"
            disabled={disabled}
            className={cn(validationErrors.country ? 'border-red-500' : '')}
          />
          <ErrorText error={validationErrors.country} />
        </div>

        <div className="space-y-1">
          <Label htmlFor="province">Provincia (*)</Label>
          <Select
            value={clientData.province?.id?.trim() ?? undefined}
            onValueChange={(value) => handleSelectChange('province', value)}
            disabled={
              disabled ||
              !clientData.country?.id ||
              isLoadingProvinces ||
              isErrorProvinces
            }
          >
            <SelectTrigger
              className={validationErrors.province ? 'border-red-500' : ''}
              onFocus={() => handleInputFocus('province')}
            >
              <SelectValue
                placeholder={
                  isLoadingProvinces
                    ? 'Cargando...'
                    : isErrorProvinces
                      ? 'Error cargando provincias'
                      : 'Seleccionar provincia'
                }
              />
            </SelectTrigger>
            <SelectContent>
              {provinceOptions.map((option) => (
                <SelectItem key={option.value} value={option.value}>
                  {option.label}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
          <ErrorText error={validationErrors.province} />
        </div>

        <div className="space-y-1">
          <Label htmlFor="municipality">Municipio (*)</Label>
          <Select
            value={clientData.municipality?.id?.trim() ?? undefined}
            onValueChange={(value) => handleSelectChange('municipality', value)}
            disabled={
              disabled ||
              !clientData.province?.id?.trim() ||
              isLoadingMunicipalities ||
              isErrorMunicipalities
            }
          >
            <SelectTrigger
              className={validationErrors.municipality ? 'border-red-500' : ''}
              onFocus={() => handleInputFocus('municipality')}
            >
              <SelectValue
                placeholder={
                  !clientData.province?.id?.trim()
                    ? 'Selecciona provincia primero'
                    : isLoadingMunicipalities
                      ? 'Cargando...'
                      : isErrorMunicipalities
                        ? 'Error cargando municipios'
                        : 'Seleccionar municipio'
                }
              />
            </SelectTrigger>
            <SelectContent>
              {municipalityOptions.map((option) => (
                <SelectItem key={option.value} value={option.value}>
                  {option.label}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
          <ErrorText error={validationErrors.municipality} />
        </div>
      </div>
    </div>
  )
}
